import { useEffect } from 'react';

import { ENV } from '~/constants/config/env';
import { useEnrolledProducts } from '~/hooks/useEnrolledProducts';
import type { PlatformContractType, TrialPlatform } from '~/store/trial.store';
import { useTrialStore } from '~/store/trial.store';
import { getDevFlagOverrides } from '~/utils/dev-feature-flags';

/** Maps enrolled-product codes to their TrialPlatform key in the store. */
const PRODUCT_CODE_TO_PLATFORM: Record<string, TrialPlatform> = {
  'google-ads': 'google',
  'meta-ads': 'meta',
};

/**
 * Syncs per-platform contract types into trial.store from the enrolled-products API.
 * Called once in the tenant layout so the data is available app-wide after auth.
 *
 * In dev mode, when the 'trial-flow' override is active (via localStorage or
 * VITE_DEV_FEATURE_FLAGS), all known platforms are forced to TRIAL_POC so
 * developers can exercise the trial UI without a real TRIAL_POC contract.
 */
export function useTrialSync() {
  const { data: products } = useEnrolledProducts();
  const setEnrolledContracts = useTrialStore((s) => s.setEnrolledContracts);

  // Short-circuit: if trial-flow is locally overridden, treat all platforms as trial.
  const devTrialOverride = getDevFlagOverrides(ENV.VITE_DEV_FEATURE_FLAGS)['trial-flow'] === true;

  useEffect(() => {
    if (devTrialOverride) {
      setEnrolledContracts({ google: 'TRIAL_POC', meta: 'TRIAL_POC' });
      return;
    }
    // Wait for the query to resolve before writing — `products` is `undefined`
    // while loading (and on error). Writing an empty map now would mark the
    // store `isLoaded` prematurely and flash contract-gated UI (e.g. the trial
    // banner) before the real contract state is known.
    if (products === undefined) return;
    const contracts: Partial<Record<TrialPlatform, PlatformContractType | null>> = {};
    products.forEach((p) => {
      const platform = PRODUCT_CODE_TO_PLATFORM[p.productCode];
      if (platform) contracts[platform] = p.contractType as PlatformContractType | null;
    });
    setEnrolledContracts(contracts);
  }, [products, setEnrolledContracts, devTrialOverride]);
}
